import React, { useEffect, useState, useContext } from 'react';
import {
  CircularProgress,
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  IconButton,
} from '@mui/material';
import { Visibility, PictureAsPdf, ExpandMore } from '@mui/icons-material';
import PaymentIcon from '@mui/icons-material/Payment';
import { useParams, useNavigate } from 'react-router-dom';
import documentoApi from '../../api/documentoApi';
import scadenzaApi from '../../api/scadenzaApi';
import filesApi from '../../api/filesApi';
import ProtectedAction from '../../components/ProtectedAction';
import { AuthContext } from '../../auth/AuthContext';
import { scadenzaStatusMap } from '../../utils/scadenzaStatusMapper';
import { formatDate } from '../../utils/dateFormatter';
import BackButtonDesktop from '../../components/BackButtonDesktop';

export default function DocumentoDetailPageMobile() {
  const { id } = useParams();
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [doc, setDoc] = useState(null);
  const [scadenze, setScadenze] = useState([]);
  const pageSize = 1000;

  useEffect(() => {
    if (id) {
      fetchDocumento();
      fetchScadenze();
    }
  }, [id]);

  const fetchDocumento = async () => {
    try {
      const r = await documentoApi.get(id);
      setDoc(r.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchScadenze = async () => {
    if (!token) return;
    try {
      const r = await scadenzaApi.listbydocument(id, { page: 0, size: pageSize });
      setScadenze(r.data.content || []);
    } catch (err) {
      console.error('Errore caricamento scadenze', err);
      setScadenze([]);
    }
  };

  const openPdf = async (filename, contribuenteId) => {
    try {
      const response = await documentoApi.downloadDocumento(documentoId, fileName);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      console.error('Errore apertura PDF', err);
    }
  };

  const handleDownload = async (documentoId, fileName) => {
    try {
      setLoading(true);
      const response = await documentoApi.downloadDocumento(documentoId, fileName);
      const url = window.URL.createObjectURL(
                            new Blob([response.data], { type: 'application/pdf' })
                          );
      window.open(url, '_blank');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  if (!doc)
    return (
      <Container sx={{ mt: 2 }}>
        <Typography>Caricamento documento…</Typography>
      </Container>
    );

  return (
    <Container sx={{ mt: 2 }}>
    <BackButtonDesktop />
                      {loading && (
                        <Box
                          sx={{
                            position: 'fixed',
                            inset: 0,
                            bgcolor: 'rgba(255,255,255,0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1300,
                          }}
                        >
                          <CircularProgress size={48}/>
                        </Box>
                      )}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h5">{doc.tipo}</Typography>
        {doc.rateSaltate > 0 && <Badge color="error" badgeContent={doc.rateSaltate}><span /></Badge>}
      </Stack>
      <Typography variant="subtitle1">{doc.descrizione}</Typography>
      <Typography variant="body2">
        Contribuente: {doc.contribuente?.nome} {doc.contribuente?.cognome}
      </Typography>
      <Typography variant="body2">Data creazione: {formatDate(doc.dataCreazione)}</Typography>

      <Stack spacing={2} sx={{ mt: 2 }}>
        {/* PDF Principale */}
        {doc.pdfPath && (
          <Card>
            <CardContent>
              <Typography>PDF Principale</Typography>
              <IconButton
                color="error"
                size="small"
                   onClick={() => handleDownload(doc.id, doc.pdfPath)}
              >
                <PictureAsPdf />
              </IconButton>
            </CardContent>
          </Card>
        )}

        {/* Altri allegati */}
        {doc.files?.map(f => (
          <Card key={f.id}>
            <CardContent>
              <Typography>Allegato</Typography>
              <Typography variant="body2">{new Date(f.uploadDate).toLocaleString()}</Typography>
              <IconButton
                color="error"
                size="small"
                onClick={() => handleDownload(doc.id, f.filename)}
              >
                <PictureAsPdf />
              </IconButton>
            </CardContent>
          </Card>
        ))}

        <Typography variant="h6" sx={{ mt: 3 }}>
          Scadenze collegate
        </Typography>
        {scadenze.map(s => (
          <Accordion key={s.id}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', justifyContent: 'space-between' }}>
                <Typography>{s.tipo} - {formatDate(s.dataScadenza)} - €{s.importo}</Typography>
                <Chip
                  label={scadenzaStatusMap[s.stato]?.label || s.stato}
                  color={scadenzaStatusMap[s.stato]?.color || 'default'}
                  size="small"
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => navigate(`/scadenze/${s.id}`)}
                >
                  <Visibility />
                </IconButton>
                {s.pdfPath && (
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleDownload(doc.id, s.pdfPath)}
                  >
                    <PictureAsPdf />
                  </IconButton>
                )}
                {s.stato !== 'pagata' && (
                <ProtectedAction user={user} contractState={user.statoContratto}>
                  <IconButton
                    color="success"
                    size="small"
                    onClick={async () => {
                      try {
                        await scadenzaApi.setpagatamanuale(s.id);
                        fetchScadenze();
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                  >
                    <PaymentIcon />
                  </IconButton>
                  </ProtectedAction>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Container>
  );
}